import React, { useEffect } from "react";
import { useTranslation } from "react-i18next";

import InnerInput from "components/inputs/InnerInput/InnerInput";
import SwitchBtn from "components/buttons/SwitchBtn";
import DefaultDropdown from "components/dropdowns/DefaultDropdown/DefaultDropdown";
import Tooltip from "components/Tooltip";
import Checkbox from "components/inputs/Checkbox/Checkbox";
import { ImageUpload } from "components/imageUpload";
import cn from "classnames";
import {
  getNotificationAutoTriggers,
  getNotificationCategories,
  getNotificationTargetTypes,
  getNotificationTargetEntities,
  getNotificationPlaceholders,
} from "store/outAppNotifications/slice";
import { useAppDispatch, useAppSelector } from "store";
import formatCamelCase from "helpers/formatCamelCase";
import NoImage from "../../../../assets/images/image_not_found.webp";
import styles from "./PushMainTab.module.scss";

export const placeholderFallbacks: Record<string, string> = {
  firstName: "player",
  lastName: "player",
  currency: "EUR",
  country: "country",
  locale: "en-US",
  bonusTitle: "bonus",
  loyaltyLevelTitle: "level",
  loyaltyLevel: "level",
  paymentAmount: "0",
  paymentCurrency: "EUR",
  paymentStatus: "completed",
  paymentId: "payment",
  paymentCreated: "date",
};

interface PushMainTabProps {
  payload: any;
  handleData: (
    key: string,
    value: string | boolean | number,
    img?: File | null,
  ) => void;
  clearFieldError: (field: string) => void;
  fieldErrors?: Record<string, string>;
  readOnly?: boolean;
}

const PushMainTab: React.FC<PushMainTabProps> = ({
  payload,
  handleData,
  clearFieldError,
  fieldErrors,
  readOnly,
}) => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();

  const categories = useAppSelector((store) => store["out-notif"].categories);
  const triggers = useAppSelector((store) => store["out-notif"].triggers);
  const targetTypes = useAppSelector((store) => store["out-notif"].targetTypes);
  const ctaTargets = useAppSelector((store) => store["out-notif"].ctaTargets);
  const isCtaLoading = useAppSelector(
    (store) => store["out-notif"].isCtaLoading,
  );
  const placeholders = useAppSelector(
    (store) => store["out-notif"].placeholders,
  );

  const [search, setSearch] = React.useState("");
  const [deliveryChannels, setDeliveryChannels] = React.useState({
    webPush: false,
    mobilePush: false,
  });

  const toggleDeliveryChannel = (channel: keyof typeof deliveryChannels) => {
    if (readOnly) return;

    setDeliveryChannels((current) => ({
      ...current,
      [channel]: !current[channel],
    }));
  };

  useEffect(() => {
    dispatch(getNotificationCategories());
    dispatch(getNotificationAutoTriggers());
    dispatch(getNotificationTargetTypes());
  }, []);

  useEffect(() => {
    if (!payload?.targetType) return;
    if (payload?.targetType === "deposit") return;

    const timeout = setTimeout(() => {
      dispatch(
        getNotificationTargetEntities({
          targetType: payload.targetType,
          search: search || undefined,
        }),
      );
    }, 500);

    return () => clearTimeout(timeout);
  }, [payload?.targetType, search]);

  useEffect(() => {
    dispatch(getNotificationPlaceholders(payload?.category));
  }, [payload?.category]);

  const selectedItem = ctaTargets.find(
    (item) => item.value === payload?.targetId,
  );

  const finalOptions = search
    ? ctaTargets
    : selectedItem
      ? ctaTargets
      : payload?.targetId
        ? [
            {
              value: payload.targetId,
              label: payload.targetTitle || "Selected item",
              img: NoImage,
            },
            ...ctaTargets,
          ]
        : ctaTargets;

  return (
    <div className={`out-notif-content ${styles.root}`}>
      <div className="out-notif-form__column">
        <div className="out-notif-form__item">
          <InnerInput
            value={payload?.title ?? ""}
            placeholder={t("enterTitle")}
            label={t("title")}
            type="text"
            id="notification-title"
            onChange={(e) => {
              handleData("title", e);
              clearFieldError("title");
            }}
            hasError={Boolean(fieldErrors?.["title"])}
            disabled={readOnly}
          />
        </div>

        <div className="out-notif-form__item">
          <InnerInput
            value={payload?.content ?? ""}
            placeholder={t("enterContent")}
            label={t("content")}
            type="textarea"
            id="notification-content"
            tooltipText={t("usePlaceholdersInContentNotification")}
            tooltipPosition="right"
            onChange={(e) => {
              handleData("content", e);
              clearFieldError("content");
            }}
            hasError={Boolean(fieldErrors?.["content"])}
            disabled={readOnly}
          />
          <div className="out-notif-placeholders">
            {placeholders.map((item) => (
              <div key={item} className="out-notif-placeholders__item">
                {`{{ ${item} || ${placeholderFallbacks[item] || "value"} }}`}
              </div>
            ))}
          </div>
        </div>
        <div className="out-notif-form__item row">
          <DefaultDropdown
            value={payload?.category}
            placeholder={t("category")}
            label={t("category")}
            options={categories.map((item) => ({
              label: item,
              value: item,
            }))}
            onSelect={(e) => {
              handleData("category", e);
              clearFieldError("category");
            }}
            hasError={Boolean(fieldErrors?.["category"])}
            disabled={readOnly}
          />
          <InnerInput
            value={
              payload?.removeAfterHours == null
                ? ""
                : String(payload.removeAfterHours)
            }
            placeholder="24"
            label={t("removeAfterHours")}
            type="number"
            inputMode="numeric"
            id="notification-remove-after-hours"
            onChange={(e) => {
              handleData("removeAfterHours", e);
              clearFieldError("removeAfterHours");
            }}
            hasError={Boolean(fieldErrors?.["removeAfterHours"])}
            disabled={readOnly}
          />
        </div>

        <div className="out-notif-form__item row">
          <DefaultDropdown
            value={payload?.type ?? "manual"}
            placeholder={t("type")}
            label={t("type")}
            options={[
              {
                label: t("manual"),
                value: "manual",
              },
              {
                label: t("auto"),
                value: "auto",
              },
            ]}
            onSelect={(e) => {
              handleData("type", e);

              if (e === "manual") {
                handleData("trigger", "");
              }

              clearFieldError("type");
            }}
            hasError={Boolean(fieldErrors?.["type"])}
            disabled={readOnly}
          />
          <DefaultDropdown
            value={payload?.trigger}
            placeholder={
              payload?.type === "auto"
                ? t("selectedTriggerForAutoEvent")
                : t("notAvailableForManualType")
            }
            label={t("trigger")}
            options={triggers.map((item) => ({
              label: formatCamelCase(item),
              value: item,
            }))}
            onSelect={(e) => {
              handleData("trigger", e);
              clearFieldError("trigger");
            }}
            hasError={Boolean(fieldErrors?.["trigger"])}
            disabled={payload?.type !== "auto" || readOnly}
          />
        </div>

        <div className="out-notif-form__item row">
          <DefaultDropdown
            value={payload?.buttonTextKey}
            placeholder={t("buttonTextKey")}
            label={t("buttonTextKey")}
            options={[
              { value: "play", label: "Play" },
              { value: "playNow", label: "Play now" },
              { value: "join", label: "Join" },
              { value: "joinNow", label: "Join now" },
              { value: "claim", label: "Claim" },
              { value: "claimBonus", label: "Claim bonus" },
              { value: "deposit", label: "Deposit" },
              { value: "depositNow", label: "Deposit now" },
              { value: "open", label: "Open" },
              { value: "readMore", label: "Read more" },
            ]}
            onSelect={(e) => {
              handleData("buttonTextKey", e);
              clearFieldError("buttonTextKey");
            }}
            hasError={Boolean(fieldErrors?.["buttonTextKey"])}
            isTop
            disabled={readOnly}
          />
          <DefaultDropdown
            value={payload?.targetType}
            placeholder={t("targetType")}
            label={t("targetType")}
            options={targetTypes.map((item) => ({
              value: item.value,
              label: item.title,
            }))}
            onSelect={(e) => {
              handleData("targetType", e);

              handleData("targetId", 0);
              handleData("targetTitle", "");
              handleData("targetSlug", "");

              dispatch(
                getNotificationTargetEntities({
                  targetType: String(e),
                }),
              );

              clearFieldError("targetType");
            }}
            hasError={Boolean(fieldErrors?.["targetType"])}
            isTop
            disabled={readOnly}
          />
        </div>

        <div className={styles.deliveryChannels}>
          <div className={styles.deliveryChannelsTitle}>
            {t("deliveryChannels")}
          </div>
          <div className={styles.deliveryChannelsList}>
            <div className={styles.deliveryChannel}>
              <Checkbox
                value
                txt={t("inAppInbox")}
                id="notification-in-app-inbox"
                onCheck={() => undefined}
                disabled
              />
              <Tooltip text={t("inAppInboxTooltip")} position="top" />
            </div>

            <div className={styles.deliveryChannel}>
              <Checkbox
                value={deliveryChannels.webPush}
                txt={t("webPush")}
                id="notification-web-push"
                onCheck={() => toggleDeliveryChannel("webPush")}
                disabled={readOnly}
              />
              <Tooltip text={t("webPushTooltip")} position="top" />
            </div>

            <div className={styles.deliveryChannel}>
              <Checkbox
                value={deliveryChannels.mobilePush}
                txt={t("mobilePush")}
                id="notification-mobile-push"
                onCheck={() => toggleDeliveryChannel("mobilePush")}
                disabled={readOnly}
              />
              <Tooltip text={t("mobilePushTooltip")} position="top" />
            </div>
          </div>
        </div>
      </div>

      <div className="out-notif-form__column">
        <div className={styles.imageGrid}>
          <div className="out-notif-image">
            <div className="label">Desktop</div>

            <div
              className={cn("out-notif-image__wrapper", {
                "has-error": fieldErrors?.["desktopImage"],
              })}
            >
              <ImageUpload
                image={payload?.desktopImage}
                isActive={!!payload?.isActive}
                onAddImage={(file) => {
                  handleData("desktopImage", "", file);
                  clearFieldError("desktopImage");
                }}
                disabled={readOnly}
              />
            </div>
          </div>
          <div className="out-notif-image">
            <div className="label">Mobile</div>

            <div
              className={cn("out-notif-image__wrapper", {
                "has-error": fieldErrors?.["mobileImage"],
              })}
            >
              <ImageUpload
                image={payload?.mobileImage}
                isActive={!!payload?.isActive}
                onAddImage={(file) => {
                  handleData("mobileImage", "", file);
                  clearFieldError("mobileImage");
                }}
                disabled={readOnly}
              />
            </div>
          </div>
        </div>
        <div className="out-notif-form__switcher">
          <SwitchBtn
            activeTxt={t("active")}
            notActiveTxt={t("hidden")}
            isActive={!!payload?.isActive}
            handleClick={() =>
              handleData("isActive", payload?.isActive ? 0 : 1)
            }
            disabled={readOnly}
          />
        </div>
        <div className="out-notif-form__title">
          <DefaultDropdown
            value={payload?.targetId}
            placeholder={
              payload?.targetType === "deposit"
                ? "No items available"
                : "Choose Item"
            }
            label="Title"
            options={finalOptions}
            disabled={payload?.targetType === "deposit" || readOnly}
            onSelect={(value) => {
              const selected = ctaTargets.find((item) => item.value === value);

              handleData("targetId", selected?.value ?? 0);
              handleData("targetTitle", selected?.label ?? "");

              if ("slug" in (selected || {})) {
                handleData("targetSlug", selected?.slug ?? "");
              }
              clearFieldError("targetId");
            }}
            hasError={Boolean(fieldErrors?.["targetId"])}
            search={search}
            onSearch={setSearch}
            loading={isCtaLoading}
            isTop
          />
        </div>
      </div>
    </div>
  );
};

export default PushMainTab;
